import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import type { EditorState } from '@tiptap/pm/state';

/**
 * If `text` is a single bare URL — with or without a scheme — return its
 * canonical https(s) form; otherwise null. Accepts explicit `http(s)://` URLs
 * and scheme-less hosts that look like a domain (`nulanotes.com`,
 * `www.nulanotes.com`, `example.com/path`), normalizing the latter to
 * `https://`. Rejects prose, internal whitespace, non-http schemes, emails,
 * and bare words without a dotted TLD (`localhost`, `hello`).
 */
export function normalizeBareUrl(text: string): string | null {
  const t = text.trim();
  if (!t || /\s/.test(t)) return null;

  // Explicit scheme: only http(s) allowed.
  if (/^[a-z][a-z0-9+.-]*:/i.test(t)) {
    try {
      const u = new URL(t);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
      return u.toString();
    } catch {
      return null;
    }
  }

  // No scheme: must look like host.tld (optionally with a path/query), and not
  // be an email address. Require a dotted host with a 2+ char alphabetic TLD.
  if (t.includes('@')) return null;
  const host = t.split(/[/?#]/, 1)[0];
  if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(host)) return null;
  try {
    return new URL(`https://${t}`).toString();
  } catch {
    return null;
  }
}

/** True when the trimmed text is a single bare URL (scheme or scheme-less). */
export function isBareUrl(text: string): boolean {
  return normalizeBareUrl(text) !== null;
}

/** True when the selection sits in an empty paragraph (nothing typed yet). */
function isInEmptyParagraph(state: EditorState): boolean {
  const { $from, empty } = state.selection;
  if (!empty) return false;
  const parent = $from.parent;
  return parent.type.name === 'paragraph' && parent.content.size === 0;
}

/** True when the selection is anywhere inside a list item. */
export function isInList(state: EditorState): boolean {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if ($from.node(depth).type.name === 'listItem') return true;
  }
  return false;
}

const linkUnfurlPasteKey = new PluginKey('linkUnfurlPaste');

/**
 * Auto-unfurl: when a lone URL is pasted at the start of an empty paragraph
 * (and not inside a list), convert it into a `linkUnfurl` card node instead of
 * plain link text. Defers (returns false) for anything else, so normal paste —
 * including YoutubePaste (which runs first at higher priority and embeds bare
 * YouTube URLs) and MarkdownPaste — is unaffected.
 *
 * Mirrors the Campsite PasteHandler conditions: single URL, empty paragraph,
 * not in a list. Requires the LinkUnfurl node to be registered.
 */
export const LinkUnfurlPaste = Extension.create({
  name: 'linkUnfurlPaste',
  // Below YoutubePaste (1000) so YouTube still embeds, above default paste.
  priority: 500,

  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        key: linkUnfurlPasteKey,
        props: {
          handlePaste(view: EditorView, event: ClipboardEvent) {
            const text = event.clipboardData?.getData('text/plain') ?? '';
            const normalized = normalizeBareUrl(text);
            if (!normalized) return false;
            const { state } = view;
            if (isInList(state) || !isInEmptyParagraph(state)) return false;
            if (!state.schema.nodes.linkUnfurl) return false;
            editor.commands.insertLinkUnfurl(normalized);
            return true;
          },
        },
      }),
    ];
  },
});
